import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Boxes, Gauge, LogOut, ArrowRight, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { applyTheme } from "@/lib/theme";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { isGlobal, roleInPortal } from "@/lib/roles";
import { Btn, Loader } from "@/components/portal/ui";
import { LanguageSwitcher } from "@/components/portal/LanguageSwitcher";

export default function PortalSelect() {
  const { user, loading, logout } = useAuth();
  const { t, L } = useI18n();
  const navigate = useNavigate();
  const [portals, setPortals] = useState(null);

  useEffect(() => {
    api.get("/portals/_control/config").then((r) => applyTheme(r.data.theme)).catch(() => {});
    api.get("/portals").then(({ data }) => setPortals(data)).catch(() => setPortals([]));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><Loader /></div>;
  if (!user) return <Navigate to="/" replace />;

  const global = isGlobal(user);
  const keys = [...new Set((user.memberships || []).filter((m) => (m.status || "active") === "active").map((m) => m.portal_key))];
  const destCount = (global ? 1 : 0) + keys.length;

  if (destCount === 1) {
    return <Navigate to={global ? "/control" : `/p/${keys[0]}/dashboard`} replace />;
  }
  if (portals === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><Loader /></div>;

  const byKey = Object.fromEntries(portals.map((p) => [p.portal_key, p]));

  const Header = () => (
    <header className="h-16 flex items-center border-b" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
      <div className="w-full max-w-5xl mx-auto px-6 flex items-center gap-3">
        <div className="flex items-center gap-2 font-heading text-xl font-bold" style={{ color: "var(--text)" }}>
          <Boxes className="h-6 w-6" style={{ color: "var(--accent)" }} /> KEWAGPortal
        </div>
        <div className="ml-auto flex items-center gap-3">
          <LanguageSwitcher />
          <span className="hidden sm:block text-sm" style={{ color: "var(--text-muted)" }}>{user.name}</span>
          <Btn variant="ghost" onClick={logout} className="h-10 px-3" data-testid="select-logout"><LogOut className="h-4 w-4" /></Btn>
        </div>
      </div>
    </header>
  );

  if (destCount === 0) {
    return (
      <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="portal-select">
        <Header />
        <div className="max-w-5xl mx-auto px-6 py-24 flex flex-col items-center gap-4 text-center">
          <ShieldCheck className="h-10 w-10" style={{ color: "var(--accent)" }} />
          <p style={{ color: "var(--text)" }}>{L({ de: "Dein Konto ist noch keinem Portal zugeordnet.", fr: "Votre compte n’est lié à aucun portail.", it: "Il tuo account non è ancora associato a un portale.", en: "Your account is not linked to any portal yet." })}</p>
          <Btn variant="outline" onClick={logout}><LogOut className="h-4 w-4" /> {t("nav.logout")}</Btn>
        </div>
      </div>
    );
  }

  const Tile = ({ testid, title, subtitle, color, onClick }) => (
    <button onClick={onClick} data-testid={testid}
      className="group text-left border rounded-xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl kw-fade-up"
      style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
      <div className="h-2" style={{ background: color }} />
      <div className="p-6">
        <div className="font-heading text-2xl font-bold" style={{ color: "var(--text)" }}>{title}</div>
        <p className="mt-1 text-sm min-h-[1.5rem]" style={{ color: "var(--text-muted)" }}>{subtitle}</p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium transition-transform group-hover:translate-x-1" style={{ color: "var(--accent)" }}>
          {t("control.openPortal")} <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </button>
  );

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="portal-select">
      <Header />
      <section className="max-w-5xl mx-auto px-6 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold" style={{ color: "var(--text)" }}>
          {L({ de: "Portal wählen", fr: "Choisir le portail", it: "Scegli il portale", en: "Choose portal" })}
        </h1>
        <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
          {L({ de: "Wähle das Cockpit, das du öffnen möchtest.", fr: "Sélectionnez le cockpit à ouvrir.", it: "Seleziona il cockpit da aprire.", en: "Select the cockpit you want to open." })}
        </p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {global && (
            <Tile testid="select-control" title={t("control.title")} color="var(--accent)"
              subtitle={L({ de: "Zentrale Steuerung aller Portale", fr: "Pilotage central", it: "Controllo centrale", en: "Central control of all portals" })}
              onClick={() => navigate("/control")} />
          )}
          {keys.map((k) => {
            const p = byKey[k];
            const role = roleInPortal(user, k);
            const label = role && p?.role_labels?.[role] ? L(p.role_labels[role]) : role ? t(`roles.${role}`) : L(p?.tagline);
            return (
              <Tile key={k} testid={`select-portal-${k}`} title={p?.name || k} color={p?.theme?.primary || "var(--primary)"}
                subtitle={label} onClick={() => navigate(`/p/${k}/dashboard`)} />
            );
          })}
        </div>
      </section>
    </div>
  );
}
